import * as React from "react";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import {
    BoardViewModel,
    createGameAtMove,
    createGameFromBoard,
    DEFAULT_ANIMATION_INTERVAL,
    GameRecord,
    getBoardAfterMove,
    describeMove,
    getClockAtMove,
    getPlayerDisplayName,
    PitAnimator,
    PlayerSummary,
    Theme,
} from "@mancala/core";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import { Context } from "../../context/context";
import useMoveKeys from "../../hooks/useMoveKeys";
import { createBoardViewModel } from "../../util/BoardViewModels";
import { formatDate, formatDuration, getResultPillClass, getResultText, getViewerSeat } from "../../util/GameResultText";
import BoardView from "../board/BoardView";
import PageContainer from "../PageContainer";
import AppHeader from "../headerbar/AppHeader";
import { FirstIcon, LastIcon, NextIcon, PauseIcon, PlayIcon, PlusIcon, PreviousIcon, ReplayIcon } from "../Icons";
import PlayerCard from "../game/PlayerCard";
import GameClockView from "../GameClockView";
import MoveList from "./MoveList";
import MoveTags from "./MoveTags";
import { GAME_STEP_GAME_MOVE } from "mancala.js";
import "../../styles/game.css";
import "./ReplayView.css";

const SPEEDS = [1, 2, 4];
// Extra pause after a move's animation while playing, at 1x.
const PAUSE_BETWEEN_MOVES_MS = 700;
const PLAYER1_STORE = 6;
const PLAYER2_STORE = 13;

function readMoveFromUrl(total: number): number {
    const value = new URLSearchParams(window.location.search).get("move");
    const move = value === null ? NaN : Number(value);
    return Number.isInteger(move) && move >= 0 && move <= total ? move : total;
}

// Replay of a finished game: starts at the final position; moves can be stepped through, played at 1x/2x/4x or
// jumped to from the timeline and the move list. Players see the board from their own side.
const ReplayView: FunctionComponent<{ context: Context, theme: Theme, record: GameRecord, userKey?: string }> = (
    { context, theme, record, userKey }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const moves = record.moves;
    const total = moves.length;
    const viewerId = getViewerSeat(record, userKey) ? userKey : undefined;
    const revert = getViewerSeat(record, userKey) === "player2";

    const baseGame = useMemo(() => {
        const game = createGameFromBoard({
            id: record.id, player1Id: record.player1.userId, player2Id: record.player2.userId,
            turnPlayerId: moves[total - 1]?.playerId ?? record.player1.userId, board: record.board,
        });
        game.mancalaGame.state = "ended";
        return game;
    }, [record.id]);

    const [moveNumber, setMoveNumber] = useState<number>(() => readMoveFromUrl(total));
    const [playing, setPlaying] = useState<boolean>(false);
    const [speed, setSpeed] = useState<number>(1);
    const [boardViewModel, setBoardViewModel] = useState<BoardViewModel>(
        () => createBoardViewModel(getBoardAfterMove(moves, moveNumber), theme));
    const animatorRef = useRef<PitAnimator>();

    useEffect(() => {
        // The animator changes its view model in place, so pass React a new object for every frame.
        const animator = new PitAnimator(context.themeManager,
            (viewModel) => setBoardViewModel({ id: v4(), pits: [...viewModel.pits] } as BoardViewModel));
        animator.setNewGame(createGameAtMove(baseGame, moves, moveNumber));
        animatorRef.current = animator;
        return () => animator.dispose();
    }, [baseGame]);

    useEffect(() => {
        window.history.replaceState(null, "", `${window.location.pathname}?move=${moveNumber}`);
    }, [moveNumber]);

    // Shows a position without animation.
    const showMove = (n: number) => {
        const target = Math.min(Math.max(n, 0), total);
        const animator = animatorRef.current;
        if (animator) {
            animator.clearCurrentInterval();
            animator.setNewGame(createGameAtMove(baseGame, moves, target));
        }
        setMoveNumber(target);
    };

    // Plays the next move with the same animation as a live game.
    const animateNextMove = () => {
        const animator = animatorRef.current;
        if (!animator || moveNumber >= total) return;
        animator.clearCurrentInterval();
        animator.animationInterval = DEFAULT_ANIMATION_INTERVAL / speed;
        animator.setNewGame(createGameAtMove(baseGame, moves, moveNumber));
        animator.setUpdatedGame(createGameAtMove(baseGame, moves, moveNumber + 1));
        setMoveNumber(moveNumber + 1);
    };

    const jumpTo = (n: number) => {
        setPlaying(false);
        showMove(n);
    };

    const togglePlay = () => {
        if (playing) {
            setPlaying(false);
        } else {
            if (moveNumber >= total) showMove(0);
            setPlaying(true);
        }
    };

    useEffect(() => {
        if (!playing) return;
        if (moveNumber >= total) {
            setPlaying(false);
            return;
        }
        // Wait for the previous move's animation (one step per sown stone and rule event) plus a pause.
        const previousSteps = moveNumber > 0 ? moves[moveNumber - 1].gameSteps.length + 1 : 0;
        const delay = (previousSteps * DEFAULT_ANIMATION_INTERVAL + PAUSE_BETWEEN_MOVES_MS) / speed;
        const timer = setTimeout(animateNextMove, delay);
        return () => clearTimeout(timer);
    }, [playing, moveNumber, speed]);

    useMoveKeys({
        previous: () => jumpTo(moveNumber - 1),
        next: () => { setPlaying(false); animateNextMove(); },
        togglePlay,
    });

    const name = (player: PlayerSummary) => getPlayerDisplayName(player, t) ?? t("Anonymous");
    const playerName = (playerId: string) =>
        name(playerId === record.player1.userId ? record.player1 : record.player2);
    const stones = getBoardAfterMove(moves, moveNumber);
    const [topPlayer, topStore, bottomPlayer, bottomStore] = revert
        ? [record.player1, PLAYER1_STORE, record.player2, PLAYER2_STORE]
        : [record.player2, PLAYER2_STORE, record.player1, PLAYER1_STORE];
    const atEnd = moveNumber >= total;
    const shownMove = moveNumber > 0 ? moves[moveNumber - 1] : undefined;
    const nextMove = moves[moveNumber];
    // Where the shown move's last stone landed; not while a move is being animated.
    const isAnimating = boardViewModel.pits.some((pit) => pit.highlight);
    const sownSteps = shownMove?.gameSteps.filter((step) => step.type === GAME_STEP_GAME_MOVE) ?? [];
    const landIndex = !isAnimating && sownSteps.length > 0
        ? sownSteps[sownSteps.length - 1].index % stones.length : undefined;
    // Each player's clock after the shown move; games saved before the game clock have none.
    const clockTimes = getClockAtMove(record, moveNumber);
    const renderClock = (player: PlayerSummary) => clockTimes && (
        <GameClockView running={false}
            timeMs={player === record.player1 ? clockTimes.player1Ms : clockTimes.player2Ms} />
    );
    const playerCard = (player: PlayerSummary, storeIndex: number, alignRight: boolean) => (
        <PlayerCard
            name={name(player)}
            isGuest={player.isGuest}
            details={[
                viewerId ? t(player.userId === viewerId ? "You" : "Opponent") : "",
                t("StoreCount", { count: stones[storeIndex] }),
            ].filter(Boolean).join(" · ")}
            active={nextMove?.playerId === player.userId}
            clock={renderClock(player)}
            alignRight={alignRight} />
    );

    const controlButton = (label: string, icon: React.ReactNode, onClick: () => void, disabled = false) => (
        <button className="tb" aria-label={label} disabled={disabled} onClick={onClick}>{icon}</button>
    );

    return (
        <PageContainer>
            <AppHeader context={context}>
                <button className="btn pri ic-m" aria-label={t("NewGame")} onClick={() => navigate("/loby")}>
                    <PlusIcon /><span className="hide-m">{t("NewGame")}</span>
                </button>
            </AppHeader>

            <main className="wrap">
                <section className="sum" aria-label={t("GameSummary")}>
                    <div className="sum-info">
                        <span className={"pill " + getResultPillClass(record, viewerId)}>{getResultText(record, viewerId, t)}</span>
                        <div className="sum-meta">
                            {[formatDate(record.endedAt, i18n.language),
                                `${t("Duration")} ${formatDuration(record.endedAt - record.startedAt)}`,
                                t("MoveCount", { count: total })].join(" · ")}
                        </div>
                    </div>
                    <div className="sum-vs">
                        <span className="pn">{name(topPlayer)}</span>
                        <span className="big">{`${record.board[topStore]} – ${record.board[bottomStore]}`}</span>
                        <span className="pn r">{name(bottomPlayer)}</span>
                    </div>
                    {total > 0 && (
                        <button className="btn pri" onClick={() => { showMove(0); setPlaying(true); }}>
                            <ReplayIcon />{t("WatchFromStart")}
                        </button>
                    )}
                </section>

                <div className="gl">
                    <div className="a-opp">{playerCard(topPlayer, topStore, false)}</div>
                    <div className="a-turn"><div className="turn" role="status">{t("MoveOf", { number: moveNumber, total })}</div></div>
                    <div className="a-me">{playerCard(bottomPlayer, bottomStore, true)}</div>
                    <div className="a-board">
                        <BoardView
                            game={createGameAtMove(baseGame, moves, moveNumber)}
                            context={context}
                            boardViewModel={boardViewModel}
                            revert={revert}
                            topName={name(topPlayer)}
                            bottomName={name(bottomPlayer)}
                            lastMoveIndex={shownMove?.moveIndex}
                            landIndex={landIndex} />
                    </div>
                    <div className="a-moves">
                        <section className="ctl" aria-label={t("PlaybackControls")}>
                            <div className="ctlrow">
                                <div className="tbtns">
                                    {controlButton(t("FirstMove"), <FirstIcon />, () => jumpTo(0), moveNumber === 0)}
                                    {controlButton(t("PreviousMove"), <PreviousIcon />, () => jumpTo(moveNumber - 1), moveNumber === 0)}
                                    <button className="tb big" aria-label={playing ? t("Pause") : t("Play")} disabled={total === 0} onClick={togglePlay}>
                                        {playing ? <PauseIcon /> : <PlayIcon />}
                                    </button>
                                    {controlButton(t("NextMove"), <NextIcon />, () => { setPlaying(false); animateNextMove(); }, atEnd)}
                                    {controlButton(t("LastMove"), <LastIcon />, () => jumpTo(total), atEnd)}
                                </div>
                                <div className="speed">
                                    <span>{t("Speed")}</span>
                                    <div className="segs" role="group" aria-label={t("PlaybackSpeed")}>
                                        {SPEEDS.map((s) => (
                                            <button key={s} className={"seg" + (s === speed ? " on" : "")} aria-pressed={s === speed}
                                                onClick={() => setSpeed(s)}>{`${s}x`}</button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <input className="range" type="range" min={0} max={total} value={moveNumber}
                                aria-label={t("MovePosition")} onChange={(event) => jumpTo(Number(event.target.value))} />
                            <div className="desc">
                                <b>{moveNumber === 0 ? t("Start") : t("MoveNumber", { number: moveNumber })}</b>
                                <span>{shownMove
                                    ? `${playerName(shownMove.playerId)} · ${t("PitNumber", { pit: describeMove(moves, moveNumber).pit })}`
                                    : t("StartPosition")}</span>
                                {shownMove && <MoveTags moves={moves} moveNumber={moveNumber} />}
                            </div>
                        </section>

                        <MoveList
                            moves={moves}
                            selectedMove={moveNumber}
                            onSelect={jumpTo}
                            playerName={playerName}
                            accentPlayerId={viewerId ?? bottomPlayer.userId} />
                    </div>
                </div>
            </main>
        </PageContainer>
    );
};

export default ReplayView;
